import {
  breadboxAPI,
  cached,
  evaluateContextPersisted,
  getDimensionTypeIdentifiersPersisted,
} from "@depmap/api";
import {
  correlationMatrix,
  linregress,
  pearsonr,
  spearmanr,
} from "@depmap/statistics";
import {
  DataExplorerContextV2,
  DataExplorerContextVariable,
  DataExplorerDatasetDescriptor,
  DataExplorerFilters,
  DataExplorerMetadata,
  DataExplorerPlotConfigDimension,
  DataExplorerPlotResponse,
  DataExplorerPlotResponseDimension,
  Dataset,
  FilterKey,
  isValidSliceQuery,
  MatrixDataset,
  SliceQuery,
} from "@depmap/types";
// Deep imports: the `@depmap/selects` package root re-exports React components.
import {
  resolveDisplayLabel,
  serializeSliceQuery,
} from "@depmap/selects/src/components/AnnotationSelect/sliceQueryUtils";
import {
  buildDimTypeMap,
  buildTablesByDim,
} from "@depmap/selects/src/components/AnnotationSelect/schemaHelpers";
import {
  isCompleteDimension,
  isExpansionDimension,
  isSampleType,
} from "../../utils/dimensionHelpers";
import { fetchDatasetIdentifiers } from "./identifiers";
import {
  fetchAxisLabel,
  fetchDatasetLabel,
  fetchEntitiesLabel,
  fetchValueType,
} from "./labels";
import { getDimensionDataWithoutLabels, prefetch } from "./helpers";

type VarEqualityExpression = { "==": [{ var: string }, string] };

const isPrimaryMetatadata = (dataset?: Dataset) => {
  if (dataset?.format !== "tabular_dataset") {
    return false;
  }

  return dataset.given_id === `${dataset.index_type_name}_metadata`;
};

/**
 * Build the full set of metadata SliceQueries to fetch for a plot. This
 * includes user-selected metadata (e.g. color_property), hardcoded extra
 * metadata for hover tips, and variables extracted from filter contexts.
 *
 * Deduplication is handled in one place via serializeSliceQuery: entries
 * are added in priority order (user selections > extra metadata > context
 * vars) and later entries that match an already-tracked query are skipped.
 */
export function buildExtendedMetadata(
  index_type: string,
  index_id_column?: string,
  metadata?: DataExplorerMetadata,
  filters?: DataExplorerFilters
): DataExplorerMetadata {
  const result = {} as DataExplorerMetadata;
  const seen = new Set<string>();

  // Helper: add a query under `key` if it's not already tracked.
  const addIfNew = (key: string, query: SliceQuery) => {
    const serialized = serializeSliceQuery(query);
    if (!seen.has(serialized)) {
      seen.add(serialized);
      result[key] = query;
    }
  };

  // 1. User-selected metadata has highest priority. Each key here (e.g.
  // `color_property`, `facet_property`) is read by exact name downstream —
  // never dedupe one against another via `addIfNew`, even if two of them
  // happen to reference the identical slice (e.g. color_by and facet_by
  // both backed by the same annotation). `addIfNew` would silently skip
  // whichever key is processed second, dropping it from the response and
  // breaking whichever axis reads it by name — while still leaving the
  // *other* axis working, since its key won the dedup. Object key order
  // (and therefore which axis "wins") differs between a live edit and a
  // freshly-parsed URL, which is why this bug looked flaky. Every
  // user-selected key is written unconditionally; we still record its
  // serialized value in `seen` so sections 2/3 below (which are genuinely
  // supplementary/anonymous) don't redundantly re-fetch the same slice
  // under a new key.
  if (metadata) {
    for (const [key, entry] of Object.entries(metadata)) {
      if ("dataset_id" in entry) {
        seen.add(serializeSliceQuery(entry as SliceQuery));
        result[key] = entry as SliceQuery;
      } else {
        // Legacy entries (e.g. { slice_id }) — pass through as-is.
        result[key] = entry;
      }
    }
  }

  // 2. Hardcoded extra metadata for depmap_model hover tips.
  if (index_type === "depmap_model") {
    addIfNew("extra1", {
      dataset_id: "depmap_model_metadata",
      identifier_type: "column",
      identifier: "OncotreeLineage",
    });

    addIfNew("extra2", {
      dataset_id: "depmap_model_metadata",
      identifier_type: "column",
      identifier: "OncotreePrimaryDisease",
    });
  }

  // 3. Variables extracted from filter contexts (lowest priority — these
  //    are supplementary hover-tip info and should not duplicate anything
  //    already tracked above).
  let contextVarIndex = 0;
  for (const filter of Object.values(
    filters || {}
  ) as DataExplorerContextV2[]) {
    for (const variable of Object.values(filter.vars)) {
      if (
        (variable.identifier === "label" && !variable.reindex_through) ||
        variable.identifier === index_id_column
      ) {
        continue;
      }

      const asSliceQuery: SliceQuery = {
        dataset_id: variable.dataset_id,
        identifier: variable.identifier,
        identifier_type: variable.identifier_type,
        ...(variable.reindex_through
          ? { reindex_through: variable.reindex_through }
          : {}),
      };

      const serialized = serializeSliceQuery(asSliceQuery);
      if (!seen.has(serialized)) {
        seen.add(serialized);
        result[`context_var_${contextVarIndex}`] = asSliceQuery;
        contextVarIndex += 1;
      }
    }
  }

  return result;
}

export async function fetchPlotDimensions(
  index_type: string,
  dimensions: Record<string, DataExplorerPlotConfigDimension>,
  filters?: DataExplorerFilters,
  metadata?: DataExplorerMetadata
): Promise<DataExplorerPlotResponse> {
  // Pre-fetch data we know we'll need below. This only works because
  // postJson() caches the Promises. When we later repeat each fetch with an
  // `await`, we're actually awaiting an inflight promise, effectively making
  // all these requests concurrent.
  prefetch(
    cached(breadboxAPI).getDimensionTypes(),
    fetchDatasetLabel(dimensions.x?.dataset_id),
    fetchDatasetLabel(dimensions.y?.dataset_id),
    fetchDatasetLabel(dimensions.color?.dataset_id),
    fetchDatasetLabel(dimensions.facet?.dataset_id)
  );

  const dimTypes = await cached(breadboxAPI).getDimensionTypes();
  const indexDimType = dimTypes.find((t) => t.name === index_type);
  const index_id_column = indexDimType?.id_column;
  const index_display_name = indexDimType?.display_name;

  const extendedMetadata = buildExtendedMetadata(
    index_type,
    index_id_column,
    metadata,
    filters
  );

  const dimensionKeys = Object.keys(dimensions).filter((key) => {
    return isCompleteDimension(dimensions[key]);
  });

  const filterKeys = Object.keys(filters || {}) as FilterKey[];

  // TODO: Recreate this hack when index_type === 'depmap_model'
  // https://github.com/broadinstitute/depmap-portal/blob/5099618/frontend/packages/portal-frontend/src/data-explorer-2/deprecated-api.ts#L412-L435
  const metadataKeys = Object.keys(extendedMetadata || {});

  // Tracks every distinct label seen across the dimension fetches. Drives
  // `index_labels` (and, via `labelToIdMapping`, `index_ids`) in the
  // assembled response.
  const uniqueLabels = new Set<string>();

  // Tracks the label→id correspondence for every (label, id) pair seen
  // during dimension fetches. Used to populate `index_ids` in the
  // response by mapping `uniqueLabels` to their corresponding IDs.
  const labelToIdMapping = {} as Record<string, string>;

  async function fetchRawDimension(key: string) {
    const { context, dataset_id, slice_type } = dimensions[key];

    const idToLabelMapping = await (() => {
      return fetchDatasetIdentifiers(
        index_type,
        dataset_id
      ).then((identifiers) =>
        Object.fromEntries(identifiers.map(({ id, label }) => [id, label]))
      );
    })();

    const [variable, identifier] = (context.expr as VarEqualityExpression)[
      "=="
    ];

    const sliceTypeIsSampleType = await isSampleType(slice_type);

    const identifier_type = (() => {
      if (variable.var === "entity_label" && slice_type !== "depmap_model") {
        return sliceTypeIsSampleType ? "sample_label" : "feature_label";
      }

      return sliceTypeIsSampleType ? "sample_id" : "feature_id";
    })();

    return getDimensionDataWithoutLabels({
      dataset_id,
      identifier,
      identifier_type,
    }).then(({ ids, values }) => {
      const indexed_values: Record<
        string,
        string | string[] | number | null
      > = {};

      for (let i = 0; i < ids.length; i++) {
        const id = ids[i];
        const label = idToLabelMapping[id];
        uniqueLabels.add(label);
        labelToIdMapping[label] = id;

        indexed_values[id] = values[i];
      }

      return {
        property: "dimensions",
        indexed_values,
        key,
      };
    });
  }

  async function fetchAggregatedDimension(key: string) {
    const { aggregation, context, dataset_id, slice_type } = dimensions[key];

    if (aggregation === "expansion") {
      throw new Error(
        `Dimension "${key}" carries the "expansion" sentinel on \`aggregation\`, ` +
          `which is not a real aggregation — its per-pair values come from ` +
          `fetchExpandedPlot, not from aggregating a slice. Expansion axes must ` +
          `not be routed through the Breadbox aggregate path; this dimension was ` +
          `mis-routed.`
      );
    }

    if (aggregation === "first" || aggregation === "correlation") {
      throw new Error(
        `aggregation "${aggregation}" is not supported by Breadbox!`
      );
    }

    const aggregate_by = (await isSampleType(slice_type))
      ? "samples"
      : "features";

    let ids: string[] = [];

    try {
      const result = await evaluateContextPersisted(
        (context as unknown) as DataExplorerContextV2
      );

      ids = result.ids;
    } catch (e) {
      window.dispatchEvent(
        new CustomEvent("dx2_context_eval_failed", { detail: context })
      );
      throw e;
    }

    const index_indentifiers = await fetchDatasetIdentifiers(
      index_type,
      dataset_id
    );

    const response = await cached(breadboxAPI, {
      persist: true,
    }).getMatrixDatasetData(dataset_id, {
      sample_identifier: "id",
      feature_identifier: "id",
      samples: aggregate_by === "samples" ? ids : null,
      features: aggregate_by === "features" ? ids : null,
      aggregate: { aggregate_by, aggregation },
    });

    const indexed_values = {} as Record<string, number | null>;

    index_indentifiers.forEach(({ id, label }) => {
      uniqueLabels.add(label);
      labelToIdMapping[label] = id;

      indexed_values[id] = response[aggregation][id] ?? null;
    });

    return {
      property: "dimensions",
      indexed_values,
      key,
    };
  }

  const responses = await Promise.all([
    ...dimensionKeys.map((key) => {
      return dimensions[key].axis_type === "raw_slice"
        ? fetchRawDimension(key)
        : fetchAggregatedDimension(key);
    }),

    ...filterKeys.map((key) => {
      const filter = (filters![key] as unknown) as DataExplorerContextV2;

      return evaluateContextPersisted(filter)
        .then((result) => {
          const indexed_values: Record<string, true> = {};

          for (let i = 0; i < result.ids.length; i++) {
            indexed_values[result.ids[i]] = true;
          }

          return {
            property: "filters",
            indexed_values,
            key,
          };
        })
        .catch((e) => {
          window.dispatchEvent(
            new CustomEvent("dx2_context_eval_failed", { detail: filter })
          );
          throw e;
        });
    }),

    ...metadataKeys.map(async (key) => {
      const indexed_values: Record<string, string | null> = {};

      const sliceQuery = extendedMetadata![key] as SliceQuery;
      const data = await getDimensionDataWithoutLabels(sliceQuery);
      let value_type = await fetchValueType(sliceQuery);

      const distinct = new Set<unknown>();

      for (let i = 0; i < data.values.length; i += 1) {
        const id = data.ids[i];
        const indexedValue = data.values[i];

        indexed_values[id] = indexedValue;

        if (indexedValue) {
          if (Array.isArray(indexedValue)) {
            indexedValue.forEach((v) => distinct.add(v));
          } else {
            distinct.add(indexedValue);
          }
        }
      }

      // No cardinality check here. A high-cardinality annotation costs exactly
      // what a low-cardinality one costs — one value per index entity — so
      // there was never a fetch-side reason to refuse it. All it affects is
      // whether the *picture* can be read, which the renderer decides, and can
      // then say so and offer a way to change it. Refusing here instead threw
      // away the whole plot (a saved link landed on a crash screen) over
      // something only the color or facet could not do.
      const isBinaryish =
        distinct.size <= 3 &&
        [...distinct].every((n) => n === 0 || n === 1 || n === 2);

      if (isBinaryish) {
        value_type = "categorical";
      }

      return {
        property: "metadata",
        value_type,
        indexed_values,
        key,
      };
    }),
  ]);

  // `index_labels` always carries real, human-readable labels — the
  // post-refactor contract: identity is `index_ids`, display is
  // `index_labels`.
  const index_labels = [...uniqueLabels];

  const index_ids = [...uniqueLabels].map((label) => labelToIdMapping[label]);

  return (async () => {
    const out = {
      index_type,
      index_id_column,
      index_display_name,
      index_ids,
      index_labels,
      dimensions: {} as DataExplorerPlotResponse["dimensions"],
      filters: {} as DataExplorerPlotResponse["filters"],
      metadata: {} as DataExplorerPlotResponse["metadata"],
    };

    const datasets = await cached(breadboxAPI).getDatasets();
    const dimTypeMap = buildDimTypeMap(dimTypes);
    const tablesByDim = buildTablesByDim(datasets);

    // Build per-metadata-key id->label maps for slices whose leaf identifier
    // is an id-based type (feature_id / sample_id). These let
    // `resolveDisplayLabel` surface the human-readable label for the leaf
    // instead of the raw id. Slices with other identifier types get no map
    // and fall through to existing behavior.
    const metadataIdToLabel: Record<
      string,
      Record<string, string> | undefined
    > = {};

    await Promise.all(
      Object.entries(extendedMetadata).map(async ([key, entry]) => {
        if (!isValidSliceQuery(entry as SliceQuery)) {
          return;
        }

        const sq = entry as SliceQuery;

        if (
          sq.identifier_type !== "feature_id" &&
          sq.identifier_type !== "sample_id"
        ) {
          return;
        }

        const dataset = datasets.find(
          (d) => d.id === sq.dataset_id || d.given_id === sq.dataset_id
        );

        if (!dataset || dataset.format !== "matrix_dataset") {
          return;
        }

        const dimTypeName =
          sq.identifier_type === "feature_id"
            ? dataset.feature_type_name
            : dataset.sample_type_name;

        if (!dimTypeName) {
          return;
        }

        const identifiers = await getDimensionTypeIdentifiersPersisted(
          dimTypeName
        );

        metadataIdToLabel[key] = Object.fromEntries(
          identifiers.map(({ id, label }) => [id, label])
        );
      })
    );

    const axisLabels = {
      x: await fetchAxisLabel(dimensions?.x),
      y: await fetchAxisLabel(dimensions?.y),
      color: await fetchAxisLabel(dimensions?.color),
      facet: await fetchAxisLabel(dimensions?.facet),
    } as Record<string, string>;

    const valueTypes = {
      x: await fetchValueType(dimensions?.x),
      y: await fetchValueType(dimensions?.y),
      color: await fetchValueType(dimensions?.color),
      facet: await fetchValueType(dimensions?.facet),
    } as Record<string, DataExplorerPlotResponseDimension["value_type"]>;

    const datasetLabels = {
      x: dimensions.x ? await fetchDatasetLabel(dimensions.x.dataset_id) : null,

      y: dimensions.y ? await fetchDatasetLabel(dimensions.y.dataset_id) : null,

      color: dimensions.color
        ? await fetchDatasetLabel(dimensions.color.dataset_id)
        : null,

      facet: dimensions.facet
        ? await fetchDatasetLabel(dimensions.facet.dataset_id)
        : null,
    } as Record<string, string | null>;

    responses.forEach((response) => {
      const { indexed_values, property, key } = response as {
        indexed_values: Record<string, number | null>;
        property: "dimensions" | "filters" | "metadata";
        key: string;
      };

      if (property === "dimensions") {
        let value_type = valueTypes[key];
        const values = out.index_ids.map((id) => {
          return indexed_values[id] ?? null;
        }) as number[];

        if (key === "color" || key === "facet") {
          const distinct = new Set(values.filter((v) => v != null));

          const isBinaryish =
            distinct.size <= 3 &&
            [...distinct].every((n) => n === 0 || n === 1 || n === 2);

          if (isBinaryish) {
            value_type = "categorical";
          }
        }

        const dimDataset = datasets.find(
          (d) =>
            d.id === dimensions[key].dataset_id ||
            d.given_id === dimensions[key].dataset_id
        );

        out.dimensions[key as keyof DataExplorerPlotResponse["dimensions"]] = {
          slice_type: dimensions[key].slice_type,
          dataset_id: dimensions[key].dataset_id,
          axis_label: axisLabels[key],
          dataset_label: datasetLabels[key] as string,
          value_type,
          values,
          units:
            dimDataset && "units" in dimDataset ? dimDataset.units : "unitless",
        };
      }

      if (property === "filters") {
        const boolValues = (indexed_values as unknown) as Record<
          string,
          boolean
        >;

        out.filters[key as FilterKey] = {
          name: filters![key as FilterKey]!.name,
          values: out.index_ids.map((id) => {
            return boolValues[id] ?? false;
          }),
        };
      }

      if (property === "metadata") {
        const sliceQuery = extendedMetadata![
          key
        ] as DataExplorerContextVariable;

        const dataset = datasets.find((d) => {
          return (
            d.id === sliceQuery.dataset_id ||
            d.given_id === sliceQuery.dataset_id
          );
        });

        const units = dataset && "units" in dataset ? dataset.units : undefined;
        const dataset_label =
          dataset && !isPrimaryMetatadata(dataset) ? dataset.name : undefined;

        const value_type = (response as any).value_type;
        const values = out.index_ids.map((id) => {
          return indexed_values[id] ?? null;
        });

        // HACK! I never imagined there would be continuous metadata. We'll
        // fake it too like a color/facet dimension instead.
        if (
          (key === "color_property" || key === "facet_property") &&
          value_type === "continuous"
        ) {
          const dimKey = key === "color_property" ? "color" : "facet";

          out.dimensions[dimKey] = ({
            axis_label: resolveDisplayLabel(
              sliceQuery,
              index_type,
              tablesByDim,
              dimTypeMap,
              metadataIdToLabel[key]
            ),
            dataset_id: sliceQuery.dataset_id,
            dataset_label,
            slice_type: null,
            values,
            value_type,
            units: units ?? "unitless",
          } as unknown) as DataExplorerPlotResponseDimension;

          return;
        }

        out.metadata[key] = {
          label: resolveDisplayLabel(
            sliceQuery,
            index_type,
            tablesByDim,
            dimTypeMap,
            metadataIdToLabel[key]
          ),
          sliceQuery,
          value_type,
          units,
          dataset_label,
          values,
        };
      }
    });

    return (out as unknown) as DataExplorerPlotResponse;
  })();
}

// Historically, all computations happened on the backend and were cached
// according to the corresponding endpoint. Many of those calculations now happen
// on the frontend. This function is used to cache those results.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const memoize = <T>(computeResponse: (...args: any[]) => Promise<T>) => {
  const cache: Record<string, Promise<T>> = {};

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return async (...args: any[]): Promise<T> => {
    const cacheKey = JSON.stringify(args);

    if (!cache[cacheKey]) {
      cache[cacheKey] = computeResponse(...args);
    }

    return cache[cacheKey] as Promise<T>;
  };
};

export const fetchDatasetsByIndexType = memoize(async () => {
  const datasets = await cached(breadboxAPI).getDatasets();
  const datasetsByIndexType = {} as Record<
    string,
    DataExplorerDatasetDescriptor[]
  >;

  datasets.forEach((dataset) => {
    // TODO: add support for tabular datasets
    if (dataset.format !== "matrix_dataset") {
      return;
    }

    // TODO: add support for other value types
    if (dataset.value_type !== "continuous") {
      return;
    }

    // Only supported by DimensionSelectV2.
    if (!dataset.feature_type_name) {
      return;
    }

    // This should never happen
    if (!dataset.sample_type_name) {
      return;
    }

    const {
      data_type,
      id,
      given_id,
      name,
      priority,
      units,
      sample_type_name,
      feature_type_name,
    } = dataset as MatrixDataset;

    const commonProperties = {
      data_type,
      given_id,
      id,
      name,
      priority,
      units,
    };

    datasetsByIndexType![sample_type_name] = [
      ...(datasetsByIndexType![sample_type_name] || []),
      {
        ...commonProperties,
        index_type: sample_type_name,
        slice_type: feature_type_name,
        sample_type_name,
        feature_type_name,
      },
    ];

    datasetsByIndexType![feature_type_name] = [
      ...(datasetsByIndexType![feature_type_name] || []),
      {
        ...commonProperties,
        index_type: feature_type_name,
        slice_type: sample_type_name,
        sample_type_name,
        feature_type_name,
      },
    ];
  });

  return datasetsByIndexType;
});

// HACK (LEGACY-COMPUTATION): linear regression is a fetcher-resident
// derivation, not a data fetch. It computes per-group least-squares fits
// (plus Pearson/Spearman) over the materialized points. It lives here
// because it once backed a dedicated Data Explorer endpoint; that backend
// is gone but the computation stayed put wearing the endpoint's shape. It
// is a function of DISPLAY state — it already groups by a categorical
// source (color_property / categorical color dim / color1+color2 filters)
// and already respects `visible[i]` — so it belongs in the
// render/derivation layer, not the data layer.
//
// Small multiples already exist (DataExplorerScatterPlot's facetKeys /
// computeFacetedLinReg) and facet on `facet_by` directly — facet_by is an
// independent axis with no color_by fallback, so that path never touches
// this function's color-derived categories. This function remains the
// single-panel, color-only computation: a reasonable interim, since in a
// single-panel scatter it reads as the overall trend. It's also retained
// as the basis for an opt-in global reference line (one ungrouped fit,
// neutral color, drawn over every panel) alongside the faceted lines.
export const fetchLinearRegression = memoize(
  async (
    index_type: string,
    dimensions: Record<string, DataExplorerPlotConfigDimension>,
    filters?: DataExplorerFilters,
    metadata?: DataExplorerMetadata
  ) => {
    if (Object.values(dimensions).some(isExpansionDimension)) {
      throw new Error(
        `fetchLinearRegression received a dimension carrying the "expansion" ` +
          `sentinel. Its color-faceted fit assumes one value per index entity, ` +
          `but an expansion axis is N×M (entity, member) pairs and is handled by ` +
          `the faceted regression path. Expanded plots must not reach this ` +
          `function — gate the caller on isExpansionDimension / expand_by.`
      );
    }

    const data = await fetchPlotDimensions(
      index_type,
      dimensions,
      filters,
      metadata
    );

    const xs = data.dimensions.x!.values;
    const ys = data.dimensions.y!.values;
    const visible = data.filters?.visible?.values || xs.map(() => true);
    let categories: (string | number | null)[] = xs.map(() => null);

    // These group labels are looked up in the legend's `colorMap` and
    // `hiddenLegendValues` downstream , and every legend key is a *string* —
    // the legend reads its categories through findCategoricalSlice, which
    // stringifies.
    const toLegendCategory = (v: unknown) =>
      (v?.toString() || null) as string | null;

    // Categorical only, matching the dimension branch below.
    if (
      ["categorical", "text"].includes(
        data.metadata?.color_property?.value_type as string
      )
    ) {
      categories = data.metadata!.color_property!.values.map(toLegendCategory);
    }

    if (
      ["categorical", "text"].includes(
        data.dimensions?.color?.value_type as string
      )
    ) {
      categories = data.dimensions.color!.values.map(toLegendCategory);
    }

    if (data.filters?.color1 || data.filters?.color2) {
      const name1 = data.filters?.color1?.name || null;
      const name2 = data.filters?.color2?.name || null;
      const color1 = data.filters?.color1?.values || xs.map(() => false);
      const color2 = data.filters?.color2?.values || xs.map(() => false);

      categories = xs.map((_, i) => {
        if (color1[i] && color2[i]) {
          return `Both (${name1} & ${name2})`;
        }

        if (color1[i] || color2[i]) {
          return color1[i] ? name1 : name2;
        }

        return null;
      });
    }

    const compareNullLast = (
      a: typeof categories[number],
      b: typeof categories[number]
    ) => {
      if (a === b) {
        return 0;
      }

      if (a === null) {
        return 1;
      }

      if (b === null) {
        return -1;
      }

      return a < b ? -1 : 1;
    };

    return [...new Set(categories)].sort(compareNullLast).map((category) => {
      const x: number[] = [];
      const y: number[] = [];

      for (let i = 0; i < xs.length; i += 1) {
        if (
          visible[i] &&
          category === categories[i] &&
          Number.isFinite(xs[i]) &&
          Number.isFinite(ys[i])
        ) {
          x.push(xs[i]);
          y.push(ys[i]);
        }
      }

      const pearson = pearsonr(x, y);
      const spearman = spearmanr(x, y);
      const regression = linregress(x, y);

      return {
        group_label: category as string | null,
        number_of_points: x.length,
        pearson: pearson.statistic,
        spearman: spearman.statistic,
        slope: regression.slope,
        intercept: regression.intercept,
        p_value: regression.pvalue,
      };
    });
  }
);

// HACK (LEGACY-COMPUTATION): the correlation matrix computed here (via
// correlationMatrix, plus the optional clustering reordering) is a
// fetcher-resident derivation, not a data fetch. Like the waterfall rank
// and linear regression, it lives here because it once backed a dedicated
// Data Explorer endpoint; that backend is gone but the computation stayed
// put wearing the endpoint's shape. It belongs in the render/derivation
// layer.
//
// Unlike the other two, this one is entangled with the broader
// correlation_heatmap design, which is itself regretted (it abused shared
// response fields by reinterpreting their semantics rather than
// introducing named structural concepts — see the correlation_heatmap
// plot_type). Don't extract this in isolation; it should move as part of
// reworking correlation_heatmap, not before.
const correlateDimension = memoize(
  async (
    dimension: DataExplorerPlotConfigDimension,
    index_type: string,
    filter?: DataExplorerContextV2,
    use_clustering?: boolean
  ): Promise<
    [DataExplorerPlotResponse["dimensions"]["x"], string[], string[]]
  > => {
    const { context, dataset_id, slice_type } = dimension;

    const [
      // FIXME: Rework all logic to work in terms of ids instead of labels.
      { ids, labels },
      filterIdentifiers,
      dataset_label,
    ] = await Promise.all([
      evaluateContextPersisted((context as unknown) as DataExplorerContextV2),
      filter ? evaluateContextPersisted(filter) : null,
      fetchDatasetLabel(dataset_id),
    ]);

    if (labels.length === 0 || labels.length > 100) {
      return [
        ({
          slice_type,
          values: [],
          value_type: "continuous",
          dataset_label,
          context_size: labels.length,
          units: "unitless",
          axis_label:
            labels.length === 0 ? "context produced no matches" : "cannot plot",
        } as unknown) as DataExplorerPlotResponse["dimensions"]["x"],
        [],
        [],
      ];
    }

    const correlate_by = (await isSampleType(slice_type))
      ? "samples"
      : "features";

    const requestParams = {
      sample_identifier: "label" as const,
      feature_identifier: "label" as const,
      samples:
        correlate_by === "samples" ? labels : filterIdentifiers?.labels || null,
      features:
        correlate_by === "features"
          ? labels
          : filterIdentifiers?.labels || null,
    };

    const response = await cached(breadboxAPI, {
      persist: true,
    }).getMatrixDatasetData(dataset_id, requestParams);
    let data = {} as Record<string, number[]>;

    // The /datasets/matrix/<id> endpoint always returns an object where features
    // are the keys. If we're correlating by sample, we want to turn that object
    // "inside out" and make the samples the keys.
    if (correlate_by === "samples") {
      const featureValues = Object.values(response);

      data = Object.fromEntries(
        labels.map((label) => [label, featureValues.map((fv) => fv[label])])
      );
    } else {
      data = Object.fromEntries(
        Object.keys(response).map((key) => [key, Object.values(response[key])])
      );
    }

    // Build label↔id lookups from the parallel arrays returned by
    // `evaluateContext`. Used to rekey `data` from labels to IDs before
    // the correlation computation, so the entire downstream path is
    // ID-keyed (matching the rest of Data Explorer's post-refactor
    // contract). Labels are only needed at the very end for the display
    // side of the response.
    const labelToId: Record<string, string> = {};
    const idToLabel: Record<string, string> = {};
    for (let i = 0; i < labels.length; i += 1) {
      labelToId[labels[i]] = ids[i];
      idToLabel[ids[i]] = labels[i];
    }

    // Rekey `data` from label-keyed to ID-keyed. `correlationMatrix` is
    // agnostic about what its keys mean — it just preserves them through
    // any internal reordering — so handing it IDs makes the returned
    // `columns` already be IDs in cluster order, no post-hoc alignment
    // needed.
    data = Object.fromEntries(
      Object.entries(data).map(([label, values]) => [labelToId[label], values])
    );

    Object.keys(data).forEach((key) => {
      const values = data[key];
      const distinct = new Set(values);
      if (distinct.size === 1 && [...distinct][0] == null) {
        delete data[key];
      }
    });

    const { columns, matrix } = correlationMatrix(data, use_clustering);

    // `columns` is now the post-clustering ID order, aligned with the
    // rows and columns of `matrix`. Derive the parallel label array for
    // the display side of the response.
    const representedIds = columns;
    const representedLabels = columns.map((id) => idToLabel[id]);

    const isAutoNamedContext = context.name.match(/^\(\d+ selected\)/) !== null;
    const entities = await fetchEntitiesLabel(slice_type);

    let axis_label = isAutoNamedContext
      ? `correlation of ${context.name} ${entities}`
      : `correlation of ${columns.length} ${entities} (from context “${context.name}”)`;

    if (filter && filterIdentifiers) {
      const dName = filter.name;
      const dCount = filterIdentifiers.ids.length;
      const dEntities = await fetchEntitiesLabel(filter.dimension_type);

      if (typeof filter.expr === "boolean") {
        axis_label += `<br>distinguished by all ${dCount} ${dEntities}`;
      } else {
        axis_label += `<br>distinguished by ${dCount} ${dName} ${dEntities}`;
      }
    }

    const outputDimension = {
      dataset_id,
      slice_type,
      axis_label,
      dataset_label,
      // We assume `value_type` is "continuous" because it wouldn't make sense
      // to compute a correlation otherwise.
      // TODO: Add validation for this.
      value_type: "continuous" as const,
      // Correlation coefficients are dimensionless.
      units: "unitless",
      // HACK: The correlation heatmap is a special case and breaks the
      // standard dimension type. `matrix` is of type number[][] but we'll
      // cast it to number[] just to keep the types simple. Caution must be
      // taken to cast this back to number[][] when plotting the heatmap.
      values: (matrix as unknown) as number[],
    };

    return [outputDimension, representedIds, representedLabels];
  }
);

export async function fetchCorrelation(
  index_type: string,
  dimensions: Record<string, DataExplorerPlotConfigDimension>,
  // eslint-disable-next-line
  filters?: DataExplorerFilters,
  // eslint-disable-next-line
  use_clustering?: boolean
): Promise<DataExplorerPlotResponse> {
  const [x, ids, labels] = await correlateDimension(
    dimensions.x,
    index_type,
    filters?.distinguish1 as DataExplorerContextV2,
    use_clustering
  );

  const [x2Raw, x2Ids] = filters?.distinguish2
    ? await correlateDimension(
        dimensions.x,
        index_type,
        filters?.distinguish2 as DataExplorerContextV2,
        use_clustering
      )
    : [null, null];

  // When clustering is enabled, correlateDimension runs a separate clustering
  // pass for x2 that may produce a different column order than x. Without
  // realignment, x2.values[i][j] describes a different gene pair than
  // x.values[i][j] (and index_ids[i/j]), so a selected cell shows the wrong
  // value from one of the two heatmaps. Reorder x2's matrix to follow x's
  // clustering order.
  let x2 = x2Raw;
  if (x2Raw && x2Ids && use_clustering) {
    const x2Matrix = (x2Raw.values as unknown) as number[][];
    const x2IdToIndex: Record<string, number> = {};
    x2Ids.forEach((id: string, i: number) => {
      x2IdToIndex[id] = i;
    });
    const reorderedMatrix = ids.map((rowId: string) =>
      ids.map(
        (colId: string) => x2Matrix[x2IdToIndex[rowId]][x2IdToIndex[colId]]
      )
    );
    x2 = { ...x2Raw, values: (reorderedMatrix as unknown) as number[] };
  }

  const dimTypes = await cached(breadboxAPI).getDimensionTypes();
  const indexDimType = dimTypes.find((t) => t.name === index_type);
  const index_id_column = indexDimType?.id_column;
  const index_display_name = indexDimType?.display_name;

  return {
    index_type,
    index_id_column,
    index_display_name,
    index_ids: ids,
    index_labels: labels,
    dimensions: x2 ? { x, x2 } : { x },
    filters: {},
    metadata: {},
  };
}

export async function fetchWaterfall(
  index_type: string,
  dimensions: Record<string, DataExplorerPlotConfigDimension>,
  filters?: DataExplorerFilters,
  metadata?: DataExplorerMetadata
): Promise<DataExplorerPlotResponse> {
  // HACK (LEGACY-COMPUTATION): this entire function is a fetcher-resident
  // derivation, not a data fetch. It sorts the index by (category, value),
  // reprojects positions into a synthetic "rank" x dimension, and remaps
  // the config's x onto y. None of that is identity or values — it's
  // plottable geometry, and it depends on display concerns (sort order,
  // faceting). It lives here because it once backed a dedicated Data
  // Explorer endpoint; that backend is gone but the computation stayed put
  // wearing the endpoint's shape. It belongs in the render/derivation
  // layer. Fine to leave until something forces a change here; when that
  // happens, prefer moving it out over extending it in place.
  const unsortedData = await fetchPlotDimensions(
    index_type,
    dimensions,
    filters,
    metadata
  );

  const sortedLabels = unsortedData.index_labels
    .map(
      (label, i) =>
        [label, unsortedData.dimensions.x.values[i]] as [string, number | null]
    )
    // Sorted by value alone. This used to sort by (category, value), which
    // made color_by partition the plot into one "snake" per category — a job
    // that belongs to facet_by, and that facet_by does for this plot type
    // already (see FacetByViewOptions). Every other plot type treats color as
    // purely visual, and the waterfall's own faceted path discards this
    // ordering anyway: formatDataForWaterfall re-buckets by facet and re-sorts
    // within each cluster, so the category sort only ever survived when there
    // was no facet — the one case where the user had asked for no grouping.
    //
    // `categoryA`/`categoryB` are left in the tuple: the category is still read
    // below to decide whether there is a categorical color at all.
    .sort(([, valueA], [, valueB]) => {
      if (valueA === null) {
        return -1;
      }

      if (valueB === null) {
        return 1;
      }

      return valueA < valueB ? -1 : 1;
    })
    .map((tuple) => tuple[0]);

  const sortByReindexedLabels = <T>(values: T[]) => {
    const indexedValues = Object.fromEntries(
      unsortedData.index_labels.map((label, i) => [label, values[i]])
    );

    return sortedLabels.map((label) => indexedValues[label]);
  };

  const sortedDimensions: DataExplorerPlotResponse["dimensions"] = {
    // HACK: This ouput "x" dimension does not match the input "x" dimension.
    // Rather, it is a special "rank" dimension that's derived from it.
    x: {
      // Always a rank now. This was blanked whenever a categorical color
      // existed, because the axis was then a series of per-category ramps
      // rather than one ranking — which is no longer true.
      axis_label: "Rank",
      dataset_label: "",
      dataset_id: dimensions.x.dataset_id,
      slice_type: dimensions.x.slice_type,
      value_type: "continuous",
      values: Array.from({ length: sortedLabels.length }, (_, i) => i),
      // A rank axis has no units.
      units: "unitless",
    },

    // HACK: We remap the "x" dimension from the plot config onto the y axis.
    // This is for consistency with the density plot, which has a single "x"
    // dimension.
    y: {
      ...unsortedData.dimensions.x,
      values: sortByReindexedLabels(unsortedData.dimensions.x.values),
    },
  };

  // FIXME: Instead of coloring the points as one big group, we could split it
  // into smaller groups according to how the continuous values get bucketed.
  // The only reason we didn't do that before is because this waterfall
  // implementation used to live on the backend while the buckets were always
  // calculated here on the frontend.
  if (unsortedData.dimensions.color) {
    sortedDimensions.color = {
      ...unsortedData.dimensions.color,
      values: sortByReindexedLabels(unsortedData.dimensions.color.values),
    };
  }

  // Mirrors the color branch above — a continuous facet_property gets
  // redirected into dimensions.facet (see the "HACK! I never imagined
  // there would be continuous metadata" comment above), so it needs the
  // same re-sort/carry-through color gets, or it's silently dropped here
  // and the Facets panel (and any faceting at all) never sees it for a
  // continuous facet_by on a waterfall plot.
  if (unsortedData.dimensions.facet) {
    sortedDimensions.facet = {
      ...unsortedData.dimensions.facet,
      values: sortByReindexedLabels(unsortedData.dimensions.facet.values),
    };
  }

  const sortedFilters: DataExplorerPlotResponse["filters"] = {};
  const sortedMetadata: DataExplorerPlotResponse["metadata"] = {};

  Object.keys(unsortedData.filters || {}).forEach((key) => {
    const unsorted = unsortedData.filters[key as FilterKey]!;

    sortedFilters[key as FilterKey] = {
      ...unsorted,
      values: sortByReindexedLabels(unsorted.values),
    };
  });

  Object.keys(unsortedData.metadata || {}).forEach((key) => {
    const unsorted = unsortedData.metadata[key]!;

    sortedMetadata[key] = {
      ...unsorted,
      values: sortByReindexedLabels(unsorted.values),
    };
  });

  const sortedIndexIds = sortByReindexedLabels(unsortedData.index_ids);

  return {
    index_type,
    index_id_column: unsortedData.index_id_column,
    index_display_name: unsortedData.index_display_name,
    index_ids: sortedIndexIds,
    index_labels: sortedLabels,
    dimensions: sortedDimensions,
    filters: sortedFilters,
    metadata: sortedMetadata,
  };
}
